import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  GRAUS_CARTAO,
  exigeConfirmacao,
  partirCloze,
  type EstudoAtual,
  type ItemEmExecucao,
  type Marcado,
  type Veredito,
} from "@noesis/shared";
import { FalhaDaApi, pedir } from "../api.js";
import { PainelDoCaderno } from "../caderno/PainelDoCaderno.js";
import { BarraDeAcoes, type ModoDaExecucao } from "./BarraDeAcoes.js";
import { MenuDoRodape } from "./MenuDoRodape.js";
import { PainelDoAssistente } from "../componentes/PainelDoAssistente.js";
import type { AgendamentoVisivel } from "@noesis/shared";
import { Agendamento } from "../componentes/Agendamento.js";
import { useTour } from "../estado/tour.js";
import { ExplicacaoDoItem } from "../componentes/ExplicacaoDoItem.js";
import { usarPausaDeMovimento } from "../componentes/modo-sessao.js";
import type { RegistroDaSessao } from "../componentes/FimDeSessao.js";
import { Icone } from "../componentes/Icone.js";
import { IconeBorracha, IconeIa, IconeSeguir } from "../componentes/icones.js";
import { CirculoMao, Visto } from "../componentes/Tracos.js";
import { responderComQueda } from "../offline/sincronizar.js";
import { enunciadoLegivel } from "../rotulos.js";

/**
 * A tela de execução. É ela que decide a direção **Polegar**.
 *
 * A aposta: usada em pé, no ônibus, com uma mão. Tudo que se toca vive na
 * metade de baixo, em alvos grandes; tudo que se lê vive em cima, na escala de
 * leitura. Cabe menos por tela e rola mais — rolar é barato, reler porque a
 * linha é apertada não é.
 *
 * **As duas escalas do invariante 14, lado a lado pela primeira vez.** Texto de
 * base, enunciado e explicação usam a escala de LEITURA: serifada, 19–22px,
 * entrelinha 1.6, medida controlada. Cronômetro, contadores, alternativas e
 * botões usam a escala de EXECUÇÃO: sem serifa, compacta, para decidir rápido.
 *
 * As alternativas ficaram na escala de execução de propósito, mesmo sendo
 * texto que se lê: elas são alvo de toque antes de serem leitura, e a decisão
 * entre cinco opções é comparação, não leitura corrida. É o ponto do desenho
 * que mais merece ser julgado no aparelho.
 */

/** Um identificador de resposta que nasce no navegador (invariante 12). */
function novoIdCliente(): string {
  return `cli-${crypto.randomUUID()}`;
}

export function Executar({
  atual,
  aoMudar,
  modo = "estudo",
  aoEncerrar,
  prazoEm,
  aoResponder,
  aoRegistrar,
}: {
  atual: EstudoAtual;
  /** O resumo local de cada resposta, para o fim de sessão (8d). */
  aoRegistrar?: (r: RegistroDaSessao) => void;
  aoMudar: (novo: EstudoAtual) => void;
  /** A resposta consolidou: o estado novo, antes de avançar. É onde a meta
      batida é notada, no instante da resposta e não no próximo item. */
  aoResponder?: (novo: EstudoAtual) => void;
  /** No simulado a barra muda (RN-55). O padrão é o estudo. */
  modo?: ModoDaExecucao;
  /** Encerrar a sessão, ou entregar a prova. Já confirmado (RN-54). */
  aoEncerrar?: () => void;
  /** Só no simulado: o prazo do servidor, para o regressivo (RN-55). */
  prazoEm?: string;
}) {
  /*
   * O fundo vivo para enquanto há questão na tela (ADR 035).
   *
   * Fica aqui, e não em `Estudar` e `Simulado`, porque este é o componente que
   * **é** a sessão: as duas telas o montam, e a pausa acompanha a questão em
   * vez de acompanhar a rota. Movimento periférico ao lado do que se lê é
   * exatamente o que a ADR 030 não paga.
   */
  usarPausaDeMovimento();

  const item = atual.item;
  const tour = useTour();
  const { informar } = tour;
  // O texto do passo "Na questão" muda com o tipo: certo ou errado pede C e E,
  // múltipla pede a letra (ADR 041).
  useEffect(() => {
    if (item) informar({ tipo: item.tipo });
  }, [item, informar]);
  /**
   * Qual painel de apoio está aberto — **um de cada vez** (RN-42).
   *
   * Um estado só para os dois é o que faz "abrir um fecha o outro" sair de
   * graça: com dois booleanos a exclusão viraria coordenação escrita à mão, e a
   * primeira coisa que quebra numa coordenação dessas é o caso em que os dois
   * ficam abertos ao mesmo tempo — que aqui nem chega a existir. Os dois
   * repartem a mesma coluna ao lado do enunciado, e duas não cabem (ADR 022).
   */
  const [painel, setPainel] = useState<"nenhum" | "caderno" | "assistente">("nenhum");
  /**
   * A página do caderno desta sessão: `null` é a em branco, que só vira página
   * no banco quando se escreve nela. Mora aqui, e não no painel, porque o
   * painel é desmontado ao fechar e a página tem que ser a mesma ao reabrir.
   */
  const [paginaDoCaderno, setPaginaDoCaderno] = useState<string | null>(null);


  const [escolhido, setEscolhido] = useState<Marcado | null>(null);
  const [veredito, setVeredito] = useState<Veredito | null>(null);
  const [proximo, setProximo] = useState<EstudoAtual | null>(null);
  const [agendamento, setAgendamento] = useState<AgendamentoVisivel | null>(null);
  const [pendenteDeEnvio, setPendenteDeEnvio] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  /**
   * RN-64: a sessão expirou por inatividade debaixo desta tela. O item continua
   * na frente de quem não pode mais respondê-lo, então a saída é dita e
   * oferecida — nunca um erro solto que deixa a pessoa tentando de novo.
   */
  const [expirada, setExpirada] = useState(false);
  const inicio = useRef<number>(Date.now());
  const riscadasDoItem = useRef(0);
  const enviandoRef = useRef(false);
  const alvoFeedback = useRef<HTMLDivElement | null>(null);
  const botaoAssistente = useRef<HTMLButtonElement>(null);
  /*
   * O painel foi fechado **pela pessoa**, e não pela troca de item.
   *
   * A diferença decide para onde vai o foco. Fechar pelo "Fechar" do painel tem
   * de devolver o foco ao alvo que reaparece; a troca de item também fecha o
   * painel (ele é da matéria do item), e ali roubar o foco tiraria a mão de quem
   * estava respondendo.
   *
   * O valor diz **para onde**: o caderno abre e fecha pelo cabeçalho da questão
   * desde a Fase 2, e o assistente continua no alvo flutuante do rodapé.
   */
  const devolverFoco = useRef<null | "caderno" | "assistente">(null);
  /*
   * Abrir e fechar o caderno num alvo só. Ele é o mesmo gesto pelo botão do
   * cabeçalho e pela tecla N, e por isso mora aqui e não dentro da barra.
   */
  const alternarCaderno = useCallback(() => {
    setPainel((p) => (p === "caderno" ? "nenhum" : "caderno"));
  }, []);

  /* Os painéis só existem no estudo — no simulado o caderno e o assistente
     ficam desligados (RN-55). É esta condição que decide também o menu do
     rodapé e a grade de duas colunas, e por isso ela tem nome em vez de ser
     repetida. */
  const painelAberto = painel !== "nenhum" && modo === "estudo";

  // O foco não pode cair no `body` quando o alvo que estava sob o dedo deixa de
  // existir. Roda depois da montagem do botão, que é o que a condição garante.
  useEffect(() => {
    if (painelAberto || devolverFoco.current === null) return;
    const alvo = devolverFoco.current;
    devolverFoco.current = null;
    // O do caderno vive no cabeçalho e nunca é desmontado: basta procurá-lo.
    if (alvo === "caderno") document.querySelector<HTMLElement>("[data-alvo='caderno']")?.focus();
    else {
      // O assistente tem dois alvos: o da barra, no largo, e o do canto, no
      // celular. O foco volta ao que está à vista.
      const naBarra = document.querySelector<HTMLElement>("[data-alvo='assistente']");
      (naBarra && naBarra.offsetParent !== null ? naBarra : botaoAssistente.current)?.focus();
    }
  }, [painelAberto]);

  /*
   * O feedback nasce abaixo das alternativas. Sem isto, quem responde no
   * celular vê a tela "não reagir": a explicação apareceu fora da dobra. A
   * barra de ação continua presa no rodapé — o "Próximo item" nunca fica
   * abaixo da dobra — mas a explicação precisa ser trazida à vista.
   */
  useEffect(() => {
    // No simulado não há feedback na tela para rolar até ele.
    if (!veredito || modo !== "estudo") return;
    alvoFeedback.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [veredito, modo]);

  // Cada item começa do zero: cronômetro, escolha e veredito.
  useEffect(() => {
    inicio.current = Date.now();
    riscadasDoItem.current = 0;
    setEscolhido(null);
    setVeredito(null);
    setAgendamento(null);
    setPendenteDeEnvio(false);
    setErro(null);
    /*
     * O painel do caderno **não** fecha ao trocar de item.
     *
     * Ele fechava quando a página era da matéria: como toda a sessão caía na
     * mesma página, mantê-lo aberto mostraria um texto que não tinha relação
     * nenhuma com a questão nova. Agora a página é do **assunto** (RN-43) e
     * acompanha a troca — quem abriu o caderno para escrever enquanto responde
     * teria que reabri-lo a cada item, que é exatamente o atrito que a RN-42
     * existe para tirar. Quem carrega a página do assunto novo é o próprio
     * painel, que observa o id do item.
     */
  }, [item?.id]);

  const enviar = useCallback(
    async (marcado: Marcado) => {
      // A trava do envio unico. `enviando` e estado e so vale no proximo
      // render, entao dois toques no mesmo intervalo passariam os dois pelo
      // guarda. A `ref` vale agora. A idempotencia por `idCliente` (RN-28)
      // protegeria o servidor de qualquer jeito; isto protege a tela de piscar
      // duas vezes.
      if (!item || enviandoRef.current) return;
      enviandoRef.current = true;
      setEnviando(true);
      setErro(null);
      try {
        // Sem rede, a resposta vai para a fila e o veredito sai do gabarito
        // baixado — pela mesma função pura que o servidor usa.
        const tempoMs = Date.now() - inicio.current;
        const r = await responderComQueda({ idCliente: novoIdCliente(), itemId: item.id, marcado, tempoMs }, 3);
        setVeredito(r.veredito);
        aoRegistrar?.({
          itemId: item.id,
          enunciado: enunciadoLegivel(item.enunciado),
          categoria: item.categoria,
          acertou: r.veredito.acertou,
          tempoMs,
          riscadas: riscadasDoItem.current,
        });
        setAgendamento(r.veredito.agendamento);
        setPendenteDeEnvio(r.pendente);
        if (modo === "estudo") tour.avancarSe("alts");
        if (r.atual) {
          setProximo(r.atual);
          aoResponder?.(r.atual);
        }
        // Devolvido, e não só guardado em estado: no simulado a confirmação
        // **é** o avanço, e ele acontece nesta mesma volta — `proximo` só
        // valeria no próximo render.
        return r.atual ?? null;
      } catch (e) {
        setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível registrar a resposta.");
        if (e instanceof FalhaDaApi && e.codigo === "sessao_expirada") setExpirada(true);
        // Falhou o envio, a escolha é desfeita: sem isto o item ficaria travado
        // — `escolhido` bloqueia o `marcar`, e não haveria como tentar de novo.
        setEscolhido(null);
      } finally {
        enviandoRef.current = false;
        setEnviando(false);
      }
      return undefined;
    },
    [item, aoResponder, aoRegistrar, modo, tour],
  );

  /**
   * Avanco para o proximo item.
   *
   * Offline nao ha "proximo" do servidor: a tela avanca pelo que ja tem em
   * maos, e o cursor real continua no banco (RN-10) — isto so move a tela ate
   * a sincronizacao acertar as contas. `atual` entra nas dependencias porque e
   * de la que o avanco local le o cursor.
   *
   * Recebe o próximo por parâmetro porque o simulado avança **na mesma volta**
   * em que confirma, antes de `proximo` existir como estado.
   */
  const avancarPara = useCallback(
    (prox: EstudoAtual | null) => {
      if (prox) aoMudar(prox);
      else if (atual.sessao) {
        aoMudar({
          ...atual,
          item: null,
          sessao: {
            ...atual.sessao,
            progresso: { ...atual.sessao.progresso, cursor: atual.sessao.progresso.cursor + 1 },
          },
        });
      }
    },
    [aoMudar, atual],
  );

  const avancar = useCallback(() => {
    tour.avancarSe("volta");
    avancarPara(proximo);
  }, [avancarPara, proximo, tour]);

  const confirmavel = item ? exigeConfirmacao(item.tipo) : false;

  /**
   * **Marcar é uma ação; responder é outra** (RN-05, ADR 021).
   *
   * Nos tipos com alternativa a marcar — múltipla e certo/errado — tocar só
   * escolhe, e a escolha é **trocável**: marcar outra substitui a anterior. A
   * guarda antiga (`if (veredito || escolhido || enviando)`) trancava a
   * primeira escolha, e é justamente ela que fazia do toque acidental um erro
   * irreversível: o item subia na fila de erradas por causa do polegar, e o
   * score de reincidência (RN-45) passava a priorizar por um dado que não mede
   * conhecimento nenhum.
   *
   * Nos tipos sem alternativa — cartão e cloze — nada muda: a autoavaliação e o
   * preenchimento já **são** a resposta, e o gesto consolida como sempre
   * consolidou. Quem decide qual é qual é `exigeConfirmacao`, no pacote
   * compartilhado, e não um `if` escrito aqui.
   *
   * **Isto não é a janela de desfazer de volta** (ADR 011). Não há relógio e
   * não há espera: é um gesto a mais, sob controle do usuário, e o gabarito
   * aparece no instante em que ele confirma.
   */
  const marcar = useCallback(
    (marcado: Marcado) => {
      if (veredito || enviando) return;
      if (confirmavel) {
        setEscolhido(marcado);
        return;
      }
      if (escolhido) return;
      setEscolhido(marcado);
      void enviar(marcado);
    },
    [veredito, enviando, confirmavel, escolhido, enviar],
  );

  /**
   * A confirmação: consolida e revela.
   *
   * **No simulado ela também avança** (RN-34): lá não há correção durante a
   * prova, então não existe tela de veredito para parar em cima — confirmar
   * grava e vai para a próxima questão. O gabarito sai no relatório, no fim.
   */
  const confirmar = useCallback(() => {
    if (!escolhido || veredito || enviando) return;
    void enviar(escolhido).then((prox) => {
      if (prox !== undefined && modo === "simulado") avancarPara(prox);
    });
  }, [escolhido, veredito, enviando, enviar, modo, avancarPara]);

  /**
   * Enter dá **um** passo do fluxo por vez: confirma o que está escolhido, ou
   * avança quando o veredito já está na tela. Nunca os dois no mesmo toque —
   * `veredito` separa os casos, e eles não coexistem.
   */
  useTeclaEnter(Boolean(escolhido) && !veredito && confirmavel, confirmar);
  useTeclaEnter(Boolean(veredito), avancar);

  /*
   * N abre e fecha o caderno (Fase 2).
   *
   * A letra não conflita com nada: os atalhos da execução são os dígitos (1 a n,
   * as alternativas e a autoavaliação do cartão) e o Enter. Ela fica fora de
   * campo de texto pelo mesmo `ehCampoDeTexto` dos outros — sem isso, escrever
   * "não" dentro do próprio caderno fecharia o painel na primeira letra.
   */
  useTeclaLetra("n", modo === "estudo", alternarCaderno);

  if (!item) return null;

  const cloze = item.tipo === "cloze";

  return (
    <div className={painelAberto ? "sessao sessao--com-painel" : "sessao"}>
      <div className="sessao__questao">
        <BarraDeAcoes
          atual={atual}
          item={item}
          modo={modo}
          prazoEm={prazoEm}
          aoEncerrar={() => aoEncerrar?.()}
          cadernoAberto={painel === "caderno"}
          aoAlternarCaderno={modo === "estudo" ? alternarCaderno : undefined}
          aoAbrirAssistente={modo === "estudo" ? () => setPainel("assistente") : undefined}
        />

        <article className="sessao__item">
          {item.textoBase && <TextoBase texto={item.textoBase} />}

          {cloze ? (
            <ClozeEmLinha
              item={item}
              escolhido={escolhido}
              veredito={veredito}
              marcar={marcar}
              desabilitado={Boolean(veredito) || enviando}
            />
          ) : item.conteudo.tipo === "cartao" ? null : (
            // O cartão leva a pergunta na frente do post-it, e não aqui.
            <h1 className="t-enunciado sessao__enunciado">{item.enunciado}</h1>
          )}
        </article>

        {!cloze && (
          <Resposta
            item={item}
            escolhido={escolhido}
            veredito={veredito}
            marcar={marcar}
            desabilitado={Boolean(veredito) || enviando}
            aoRiscar={(n) => {
              riscadasDoItem.current = n;
            }}
          />
        )}

        <div aria-live="polite" ref={alvoFeedback}>
          {veredito && modo === "estudo" && (
            <Feedback
              item={item}
              veredito={veredito}
              agendamento={agendamento}
              aoAjustar={setAgendamento}
              pendente={pendenteDeEnvio}
              aoPerguntar={() => setPainel("assistente")}
            />
          )}
          {erro && (
            <p className="campo__erro" role="alert">
              {erro}
            </p>
          )}
          {expirada && (
            <button className="botao" type="button" onClick={() => aoEncerrar?.()}>
              Montar uma sessão nova
            </button>
          )}
        </div>

        {/*
          O rodapé da questão: a dica de teclado à esquerda, o botão à direita.
          O Confirmar fica cinza até haver uma escolha; com o veredito na tela
          ele vira "Próximo item". No celular vira a barra fixa de 128px.
        */}
        <div className="sessao__rodape">
          <p className="t-anotacao-p sessao__dica">{enviando ? "Registrando…" : <Dica item={item} />}</p>
          {veredito && modo === "estudo" ? (
            <button className="botao sessao__principal" type="button" autoFocus onClick={avancar}>
              {proximo?.item ? "Próximo item" : "Ver o resultado"}
              <Icone glifo={IconeSeguir} papel="acao" />
            </button>
          ) : confirmavel ? (
            <button
              className="botao sessao__principal sessao__confirmar"
              type="button"
              disabled={!escolhido || enviando}
              onClick={confirmar}
            >
              {enviando ? "Registrando…" : "Confirmar"}
            </button>
          ) : cloze && !veredito ? (
            <button className="botao sessao__principal" type="submit" form="cloze-form" disabled={enviando || Boolean(escolhido)}>
              Conferir
            </button>
          ) : null}
        </div>
      </div>

      {painelAberto && painel === "caderno" && (
        <PainelDoCaderno
          item={item}
          paginaId={paginaDoCaderno}
          aoMudarPagina={setPaginaDoCaderno}
          aoFechar={() => {
            devolverFoco.current = "caderno";
            setPainel("nenhum");
          }}
        />
      )}
      {painelAberto && painel === "assistente" && (
        <PainelDoAssistente
          itemId={item.id}
          aoFechar={() => {
            devolverFoco.current = "assistente";
            setPainel("nenhum");
          }}
        />
      )}

      {!painelAberto && (
        <MenuDoRodape
          disponivel={modo === "estudo"}
          refDoAssistente={botaoAssistente}
          aoAbrirAssistente={() => setPainel("assistente")}
        />
      )}
    </div>
  );
}

/**
 * A dica da barra de ação, em duas metades.
 *
 * A primeira é o gesto, e vale em qualquer aparelho. A segunda é o atalho de
 * teclado, e só aparece onde existe teclado: `.dica-teclado` some sob ponteiro
 * grosso. **Atalho invisível não existe** — quem tem teclado precisa ver que
 * ele existe, e quem não tem não precisa ler sobre uma tecla que não tem.
 *
 * O número de alternativas sai do item, não de um literal: questão de quatro
 * alternativas dizia "1 a 5" e prometia uma tecla que não fazia nada.
 */
function Dica({ item }: { item: ItemEmExecucao }) {
  const c = item.conteudo;
  const letras = c.tipo === "multipla" ? Object.keys(c.alternativas).sort() : [];
  const teclado =
    c.tipo === "multipla"
      ? `${letras[0] ?? "A"} a ${letras.at(-1) ?? "E"} marcam, R risca, Enter confirma`
      : c.tipo === "certo_errado"
        ? "C marca certo, E marca errado, Enter confirma"
        : c.tipo === "cartao"
          ? `Enter vira, 1 a ${GRAUS_CARTAO.length} avaliam`
          : "Enter confere";
  const toque =
    c.tipo === "multipla"
      ? "toque para marcar, o traço risca"
      : c.tipo === "certo_errado"
        ? "toque em certo ou errado"
        : c.tipo === "cartao"
          ? "responda de cabeça antes de virar"
          : "maiúscula e acento não contam";
  return (
    <>
      <span className="dica-teclado">{teclado}</span>
      <span className="dica-toque">{toque}</span>
    </>
  );
}

/**
 * Texto de base recolhido por padrão a partir do segundo item que o usa: quem
 * já leu não quer rolar por ele de novo, e quem não leu precisa dele inteiro.
 */
function TextoBase({ texto }: { texto: string }) {
  return (
    <details className="texto-base">
      <summary className="texto-base__resumo">Texto de apoio</summary>
      <div className="leitura texto-base__corpo">
        {texto.split("\n\n").map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    </details>
  );
}

// ------------------------------------------------------------- respostas

function Resposta({
  item,
  escolhido,
  veredito,
  marcar,
  desabilitado,
  aoRiscar,
}: {
  item: ItemEmExecucao;
  escolhido: Marcado | null;
  veredito: Veredito | null;
  marcar: (m: Marcado) => void;
  desabilitado: boolean;
  aoRiscar: (quantas: number) => void;
}) {
  switch (item.conteudo.tipo) {
    case "multipla":
      return (
        <Multipla
          alternativas={item.conteudo.alternativas}
          escolhido={escolhido}
          veredito={veredito}
          marcar={marcar}
          desabilitado={desabilitado}
          aoRiscar={aoRiscar}
        />
      );
    case "certo_errado":
      return <CertoErrado escolhido={escolhido} veredito={veredito} marcar={marcar} desabilitado={desabilitado} />;
    case "cartao":
      return <Cartao frente={item.enunciado} verso={item.conteudo.verso} escolhido={escolhido} veredito={veredito} marcar={marcar} desabilitado={desabilitado} />;
    case "cloze":
      return null;
  }
}

/**
 * Digitando num campo, a tecla é do campo — nunca do atalho.
 *
 * Vale para os dígitos, que sequestrariam a lacuna do cloze, e vale para o
 * Enter, que precisa continuar podendo enviar o formulário da lacuna em vez de
 * avançar para um próximo item que ainda não existe.
 */
function ehCampoDeTexto(alvo: EventTarget | null): boolean {
  const el = alvo as HTMLElement | null;
  if (!el || !el.tagName) return false;
  return (
    el.tagName === "INPUT" ||
    el.tagName === "TEXTAREA" ||
    el.tagName === "SELECT" ||
    el.isContentEditable === true
  );
}

/**
 * Uma letra dispara uma ação. Hoje só o N, que abre e fecha o caderno.
 *
 * Fora de campo de texto pelo mesmo `ehCampoDeTexto` dos dígitos: sem isso,
 * escrever "não" dentro do próprio caderno fecharia o painel na primeira letra.
 * Sem modificador também — Ctrl+N é do navegador.
 */
function useTeclaLetra(letra: string, ativo: boolean, acao: () => void) {
  useEffect(() => {
    if (!ativo) return;
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat || e.isComposing) return;
      if (e.key.toLowerCase() !== letra) return;
      if (ehCampoDeTexto(e.target)) return;
      e.preventDefault();
      acao();
    };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [letra, ativo, acao]);
}

/**
 * As **letras** das alternativas disparam a escolha (Fase 3B).
 *
 * Elas não conflitam com os dígitos: a múltipla e o certo/errado passaram a ser
 * por letra, e os dígitos ficaram só com a autoavaliação do cartão (1 a 3), que
 * é o único lugar onde o número **é** a resposta.
 *
 * A letra vem da própria alternativa, não de uma tabela paralela: é a mesma que
 * está desenhada no quadrado e a mesma que o gabarito guarda.
 */
function useTeclasDeLetra(letras: readonly string[], disparar: (letra: string) => void, ativo: boolean) {
  const chave = letras.join("");
  useEffect(() => {
    if (!ativo) return;
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat || e.isComposing) return;
      if (ehCampoDeTexto(e.target)) return;
      const letra = e.key.toUpperCase();
      if (!chave.includes(letra) || letra.length !== 1) return;
      e.preventDefault();
      disparar(letra);
    };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [chave, disparar, ativo]);
}

/** Teclas 1–n disparam a n-ésima opção. Requisito de acessibilidade do CLAUDE.md. */
function useTeclas(quantas: number, disparar: (indice: number) => void, ativo: boolean) {
  useEffect(() => {
    if (!ativo) return;
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (ehCampoDeTexto(e.target)) return;
      const n = Number(e.key);
      if (Number.isInteger(n) && n >= 1 && n <= quantas) {
        e.preventDefault();
        disparar(n - 1);
      }
    };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [quantas, disparar, ativo]);
}

/**
 * Enter avança um passo do fluxo — e **um só por vez**.
 *
 * Desde que a janela de desfazer saiu (ADR 011) o único passo que a tecla dá é
 * ir para o próximo item, com o veredito na tela. O `ativo` continua sendo o
 * que garante que só um ouvinte responde.
 *
 * **Botão e link ficam de fora de propósito.** O navegador já dispara o clique
 * deles no Enter; agir aqui também faria a tecla valer duas vezes — avançar
 * dois itens no mesmo toque, pulando a explicação, que é justamente o que a
 * RN-06 existe para não deixar acontecer.
 */
function useTeclaEnter(ativo: boolean, acao: () => void) {
  useEffect(() => {
    if (!ativo) return;
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || e.repeat || e.isComposing) return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (ehCampoDeTexto(e.target)) return;
      const el = e.target as HTMLElement | null;
      if (el?.closest?.("button, a, [role='button']")) return;
      e.preventDefault();
      acao();
    };
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [ativo, acao]);
}


/**
 * As alternativas da múltipla escolha (Fase 3B).
 *
 * **Cada alternativa é um cartão de largura total**, com o quadrado da letra à
 * esquerda e o texto ao lado. A letra deixou de ser um número: a chave real da
 * alternativa é `A`…`E` — é o que o gabarito guarda, é o que a explicação cita e
 * é o que o Diagnóstico mostra em "Você marcou: B". Pintar `1` na tela e `B` no
 * relatório obrigava a pessoa a traduzir entre os dois.
 *
 * **Riscar é um alvo separado, à direita do cartão.** Ele tacha e esmaece a
 * alternativa **sem selecioná-la**, e clicar de novo desfaz. É o gesto de prova:
 * eliminar o que se sabe que está errado antes de escolher entre as que sobram.
 * Ele precisa ser um botão fora do cartão porque o cartão inteiro é o alvo de
 * escolher — um risco que também marcasse seria o contrário do que ele serve.
 *
 * **Os atalhos são as letras**, e não os dígitos. Eles não conflitam: os dígitos
 * ficam com a autoavaliação do cartão (1 a 3) e o Enter com o fluxo. A letra é a
 * mesma que está desenhada no quadrado.
 */
function Multipla({
  alternativas,
  escolhido,
  veredito,
  marcar,
  desabilitado,
  aoRiscar,
}: {
  alternativas: Partial<Record<string, string>>;
  escolhido: Marcado | null;
  veredito: Veredito | null;
  marcar: (m: Marcado) => void;
  desabilitado: boolean;
  aoRiscar: (quantas: number) => void;
}) {
  const chaves = Object.keys(alternativas).sort() as ("A" | "B" | "C" | "D" | "E")[];
  const escolhida = escolhido?.tipo === "multipla" ? escolhido.alternativa : null;
  const correta = veredito?.gabarito?.tipo === "multipla" ? veredito.gabarito.correta : null;
  const [riscadas, setRiscadas] = useState<readonly string[]>([]);

  // Riscar é da questão, não da sessão: a próxima nasce limpa.
  useEffect(() => setRiscadas([]), [alternativas]);
  useEffect(() => {
    aoRiscar(riscadas.length);
  }, [riscadas, aoRiscar]);

  useTeclasDeLetra(chaves, (chave) => marcar({ tipo: "multipla", alternativa: chave as "A" }), !desabilitado);

  const riscar = useCallback(
    (chave: string) => setRiscadas((r) => (r.includes(chave) ? r.filter((c) => c !== chave) : [...r, chave])),
    [],
  );

  /*
   * R risca (handoff 8b). A alternativa é a que tem o foco; sem foco numa
   * delas, a que está escolhida. Riscar não escolhe: é eliminar o que se sabe
   * errado antes de decidir entre o que sobra.
   */
  const riscarPelaTecla = useCallback(() => {
    const focada = (document.activeElement as HTMLElement | null)?.closest<HTMLElement>("[data-chave]")?.dataset.chave;
    const alvo = focada ?? escolhida;
    if (alvo) riscar(alvo);
  }, [escolhida, riscar]);
  useTeclaLetra("r", !desabilitado && !veredito, riscarPelaTecla);

  return (
    <ul className="alternativas" data-tour="alts">
      {chaves.map((chave) => {
        const estado =
          correta === chave ? "certa" : veredito && escolhida === chave ? "errada" : escolhida === chave ? "escolhida" : "neutra";
        const riscada = riscadas.includes(chave);
        return (
          <li
            key={chave}
            className={`alternativa alternativa--${estado}${riscada ? " alternativa--riscada" : ""}`}
            data-chave={chave}
          >
            <button
              type="button"
              className="alternativa__alvo"
              disabled={desabilitado}
              aria-pressed={escolhida === chave}
              onClick={() => marcar({ tipo: "multipla", alternativa: chave })}
              onContextMenu={(e) => {
                // Toque longo risca no celular (13b): o menu de contexto é o
                // evento que o navegador dá para o dedo parado.
                if (desabilitado || veredito) return;
                e.preventDefault();
                riscar(chave);
              }}
            >
              <span className="alternativa__letra" aria-hidden="true">
                {chave}
                {estado === "errada" && <CirculoMao className="alternativa__anel" feito />}
              </span>
              <span className="alternativa__texto">
                <span className="alternativa__marca">{alternativas[chave]}</span>
                {estado === "certa" && <Visto className="alternativa__visto" feito />}
                {riscada && <span className="so-leitor"> (riscada)</span>}
                {estado === "certa" && <span className="so-leitor"> (correta)</span>}
                {estado === "errada" && <span className="so-leitor"> (sua resposta, errada)</span>}
              </span>
            </button>

            {!veredito && (
              <button
                type="button"
                className="botao-icone alternativa__riscar"
                aria-pressed={riscada}
                aria-label={riscada ? `Desfazer o risco da alternativa ${chave}` : `Riscar a alternativa ${chave}`}
                title={riscada ? "Desfazer o risco" : "Riscar (R)"}
                disabled={desabilitado}
                onClick={() => riscar(chave)}
              >
                <Icone glifo={IconeBorracha} papel="navegacao" />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function CertoErrado({
  escolhido,
  veredito,
  marcar,
  desabilitado,
}: {
  escolhido: Marcado | null;
  veredito: Veredito | null;
  marcar: (m: Marcado) => void;
  desabilitado: boolean;
}) {
  const opcoes = [
    { valor: true, rotulo: "Certo" },
    { valor: false, rotulo: "Errado" },
  ];
  const escolhida = escolhido?.tipo === "certo_errado" ? escolhido.valor : null;
  const correta = veredito?.gabarito?.tipo === "certo_errado" ? veredito.gabarito.correta : null;

  // Trocável até confirmar, como na múltipla.
  // C e E, as iniciais de "certo" e "errado" — as letras que já estão escritas
  // nos dois alvos, como na múltipla.
  useTeclasDeLetra(["C", "E"], (l) => marcar({ tipo: "certo_errado", valor: l === "C" }), !desabilitado);

  return (
    <ul className="certo-errado" data-tour="alts">
      {opcoes.map((o) => {
        const estado =
          correta === o.valor ? "certa" : veredito && escolhida === o.valor ? "errada" : escolhida === o.valor ? "escolhida" : "neutra";
        return (
          <li key={o.rotulo}>
            <button
              type="button"
              className={`certo-errado__opcao certo-errado__opcao--${estado}`}
              disabled={desabilitado}
              aria-pressed={escolhida === o.valor}
              onClick={() => marcar({ tipo: "certo_errado", valor: o.valor })}
            >
              <span className="certo-errado__palavra">
                {o.rotulo.toLocaleLowerCase("pt-BR")}
                {estado === "errada" && <CirculoMao className="certo-errado__anel" feito />}
              </span>
              {estado === "certa" && <Visto className="certo-errado__visto" feito />}
              {estado === "certa" && <span className="so-leitor"> (correta)</span>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function Cartao({
  frente,
  verso,
  escolhido,
  veredito,
  marcar,
  desabilitado,
}: {
  frente: string;
  verso: string;
  escolhido: Marcado | null;
  veredito: Veredito | null;
  marcar: (m: Marcado) => void;
  desabilitado: boolean;
}) {
  const [virado, setVirado] = useState(false);
  useEffect(() => setVirado(false), [verso]);

  const grauEscolhido = escolhido?.tipo === "cartao" ? escolhido.autoavaliacao : null;
  const podeGraduar = virado && !desabilitado && !escolhido;

  useTeclas(GRAUS_CARTAO.length, (i) => marcar({ tipo: "cartao", autoavaliacao: GRAUS_CARTAO[i]!.grau }), podeGraduar);
  // Virar é o primeiro passo do cartão, e sem ele a rota de teclado começaria
  // com um Tab. O botão continua existindo e continua focável: Enter é atalho,
  // não substituto.
  const virar = useCallback(() => setVirado(true), []);
  useTeclaEnter(!virado && !desabilitado, virar);

  // Um post-it de verdade: as duas faces ocupam o mesmo lugar e o giro mostra o
  // verso. A face de costas fica `inert`, fora do Tab e do leitor de tela.
  return (
    <div className="cartao-postit" data-tour="alts">
      <div className="cartao-postit__giro" data-virado={virado || undefined}>
        <div className="postit cartao-postit__face" inert={virado} onClick={desabilitado ? undefined : virar}>
          <h1 className="t-enunciado cartao-postit__pergunta">{frente}</h1>
          <button className="link-mao cartao-postit__virar" type="button" onClick={virar} disabled={desabilitado}>
            <Icone glifo="sincronizar" papel="acao" />
            Virar
          </button>
        </div>
        <div className="postit cartao-postit__face cartao-postit__verso" inert={!virado} aria-live="polite">
          {virado && (
            <>
              <p className="cartao-postit__lembrete">{frente}</p>
              <div className="t-corpo cartao-postit__texto">
                {verso.split("\n\n").map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {virado && (
        <div className="cartao-postit__graus" role="group" aria-label="Como foi?">
          <span className="t-anotacao t-2">como foi?</span>
          {GRAUS_CARTAO.map((g, i) => (
            <button
              key={g.grau}
              type="button"
              className="chip-mao"
              disabled={desabilitado}
              aria-pressed={grauEscolhido === g.grau}
              title={g.ajuda}
              onClick={() => marcar({ tipo: "cartao", autoavaliacao: g.grau })}
            >
              <span className="dica-teclado cartao-postit__tecla" aria-hidden="true">
                {i + 1}
              </span>
              {g.rotulo.toLocaleLowerCase("pt-BR")}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * As lacunas dentro do enunciado (handoff 8c): a palavra se escreve no lugar
 * dela, sobre o sublinhado. Depois de conferir, a palavra fica verde ou
 * vermelha, e o gabarito aparece no feedback.
 */
function ClozeEmLinha({
  item,
  escolhido,
  veredito,
  marcar,
  desabilitado,
}: {
  item: ItemEmExecucao;
  escolhido: Marcado | null;
  veredito: Veredito | null;
  marcar: (m: Marcado) => void;
  desabilitado: boolean;
}) {
  const pedacos = useMemo(() => partirCloze(item.enunciado), [item.enunciado]);
  const lacunas = item.conteudo.tipo === "cloze" ? item.conteudo.lacunas : [];
  const [valores, setValores] = useState<Record<string, string>>({});
  useEffect(() => setValores({}), [item.id]);
  const completo = lacunas.every((l) => (valores[l.id] ?? "").trim() !== "");
  const cor = veredito ? (veredito.acertou ? " lacuna-campo--certa" : " lacuna-campo--errada") : "";

  return (
    <form
      id="cloze-form"
      data-tour="alts"
      onSubmit={(e) => {
        e.preventDefault();
        if (completo && !desabilitado && !escolhido) marcar({ tipo: "cloze", lacunas: valores });
      }}
    >
      <h1 className="t-enunciado sessao__enunciado">
        {pedacos.map((p, i) =>
          p.tipo === "texto" ? (
            <span key={i}>{p.valor}</span>
          ) : (
            <input
              key={i}
              className={`lacuna-campo${cor}`}
              aria-label={`Lacuna ${lacunas.findIndex((l) => l.id === p.id) + 1}`}
              placeholder="escreva aqui"
              value={valores[p.id] ?? ""}
              readOnly={Boolean(veredito) || Boolean(escolhido)}
              disabled={desabilitado && !veredito}
              onChange={(e) => setValores((v) => ({ ...v, [p.id]: e.target.value }))}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
            />
          ),
        )}
      </h1>
    </form>
  );
}

// -------------------------------------------------------------- feedback

/** O texto do veredito, à mão, como no handoff: "Isso, certinho." ou "A certa é a B." */
function fraseDoVeredito(item: ItemEmExecucao, veredito: Veredito): string {
  const g = veredito.gabarito;
  if (item.tipo === "cartao") return veredito.acertou ? "Isso, lembrou." : "Volta para a fila.";
  if (veredito.acertou) {
    if (g?.tipo === "cloze") return `Isso, ${g.lacunas.map((l) => l.resposta).join(", ")}.`;
    return "Isso, certinho.";
  }
  if (g?.tipo === "multipla") return `Quase. A certa é a ${g.correta}.`;
  if (g?.tipo === "certo_errado") return `A resposta é ${g.correta ? "certo" : "errado"}.`;
  if (g?.tipo === "cloze") return `A resposta é ${g.lacunas.map((l) => l.resposta).join(", ")}.`;
  return "Não foi dessa vez.";
}

/** RN-06: a explicação aparece sempre, acertando ou errando. */
function Feedback({
  item,
  veredito,
  agendamento,
  aoAjustar,
  pendente,
  aoPerguntar,
}: {
  item: ItemEmExecucao;
  veredito: Veredito | null;
  agendamento: AgendamentoVisivel | null;
  aoAjustar: (novo: AgendamentoVisivel) => void;
  pendente: boolean;
  aoPerguntar: () => void;
}) {
  if (!veredito) return null;

  return (
    <section className={veredito.acertou ? "feedback-caderno feedback-caderno--acerto" : "feedback-caderno feedback-caderno--erro"}>
      <p className="t-anotacao feedback-caderno__veredito">{fraseDoVeredito(item, veredito)}</p>

      {veredito.explicacao && <ExplicacaoDoItem markdown={veredito.explicacao} />}

      {!veredito.acertou && item.tipo !== "cartao" && (
        <button type="button" className="link-mao feedback-caderno__ia" onClick={aoPerguntar}>
          <Icone glifo={IconeIa} papel="acao" />
          por que errei?
        </button>
      )}

      {veredito.repetida && <p className="t-ajuda">Esta resposta já estava registrada. Nada foi contado duas vezes.</p>}
      {pendente && (
        <p className="t-ajuda">Guardada no aparelho: ela é enviada assim que houver sinal, e o intervalo aparece quando chegar.</p>
      )}

      {agendamento && (
        <Agendamento
          itemId={item.id}
          agendamento={agendamento}
          aoAjustar={item.tipo === "cartao" ? undefined : aoAjustar}
        />
      )}
    </section>
  );
}
